import { Tooltip } from "antd";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { IRelatedArticles } from "../../../../../../@types/Articles";
import { ICreateMessageProps } from "../../../../../../@types/Message";
import { Book } from "../../../../../../assets/icons/Book";
import { Close } from "../../../../../../assets/icons/Close";
import { SwitchOff } from "../../../../../../assets/icons/SwitchOff";
import { Button } from "../../../../../../components/Button";
import { SelectDropdown } from "../../../../../../components/SelectDropdown";
import { TextArea } from "../../../../../../components/TextArea";
import { TextField } from "../../../../../../components/TextField";
import { messageService } from "../../../../../../services/messageService";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import { ArticlePickerModal } from "../ArticlePickerModal";
import {
  ArticleInlineError,
  AttachArticleButton,
  AttachArticleRow,
  CancelButton,
  Container,
  Title,
  SubmitContainer,
  TextAreaContainer,
  TextFieldContainer,
  DropdownContainer,
  FirstRowContainer,
  BottomDropdownContainer,
  OptionsContainer,
  DeleteButton,
  OptionLabel,
  OptionsRowContainer,
  OptionsContainerScroll,
  NewOptionButton,
  RemoveArticleButton,
  SelectedArticleCard,
  SelectedArticleIcon,
  SelectedArticleLabel,
  SelectedArticleLink,
  SelectedArticleText,
  SendMethodDropdown,
  optionTextFieldStyles,
  ErrorMessage,
  ContentContainer,
} from "./CreateMessageModal.styles";

const isSmsProvider = (sendMethod: string) =>
  sendMethod.toLowerCase() === "sms";

type CreateMessageModalProps = {
  patientId: number;
  patientPhoneNumber: string | undefined;
  hasRegisteredDevices: boolean;
  setNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
  onClose: () => void;
  // EN-3040. All optional, so the "Send message" button keeps behaving exactly as before; they are
  // what the thread modal's "Custom" step needs to compose INTO a conversation and return to it.
  /** Joins this conversation instead of opening a new one. */
  threadId?: string;
  /** Pre-fills the subject — the thread's, which the composer must not diverge from. */
  initialTitle?: string;
  /** Carries across whatever was already typed in the thread composer. */
  initialBody?: string;
  /** Cancel and the header X. Defaults to onClose. */
  onCancel?: () => void;
  /** Success path. Defaults to onClose. */
  onSent?: () => void;
};

const useRefs = () => {
  // Keep track of references by key for the options
  const refsByKey = useRef<Record<string, HTMLInputElement | null>>({});

  const setRef = (element: HTMLInputElement | null, key: number) => {
    refsByKey.current[key] = element;
  };

  return { refsByKey: refsByKey.current, setRef };
};

export function CreateMessageModal({
  patientId,
  patientPhoneNumber,
  hasRegisteredDevices,
  setNotification,
  onClose,
  threadId,
  initialTitle,
  initialBody,
  onCancel,
  onSent,
}: CreateMessageModalProps) {
  const { t } = useTranslation();

  // Seeded once. In the thread flow these carry the subject and the half-typed draft across from
  // the conversation view, which is the whole point of the "Custom" detour.
  const [messageContent, setMessageContent] = useState(initialBody ?? "");
  const [messageTitle, setMessageTitle] = useState(initialTitle ?? "");
  const [messageType, setMessageType] = useState("FreeText");
  const [responseTimeFrame, setResponseTimeFrame] = useState("TwentyFourHours");
  const [sendMethod, setSendMethod] = useState("App");
  const initialUserEnteredOptions = [
    {
      id: 1,
      value: t("patientPage_messages_create_optionPlaceholder", { number: 1 }),
      name: "option-1",
      key: uuidv4(),
    },
  ];
  const [userEnteredOptions, setUserEnteredOptions] = useState(
    initialUserEnteredOptions
  );
  const [errorTitle, setErrorTitle] = useState("");
  const [errorOptionValue, setErrorOptionValue] = useState("");
  const { refsByKey, setRef } = useRefs();
  const [updatingRefId, setUpdatingRefId] = useState(0); // Set to 0 as this ID is not available
  const [isMessageLoading, setIsMessageLoading] = useState<boolean>(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] =
    useState<IRelatedArticles | null>(null);
  const [articleNotFoundError, setArticleNotFoundError] = useState<string>("");

  const isSms = isSmsProvider(sendMethod);

  const handleSelectArticle = (article: IRelatedArticles) => {
    setSelectedArticle(article);
    setArticleNotFoundError("");
    setIsPickerOpen(false);
    mixpanelActions.track("HCP Attached Article To Message", {
      articleId: article.articleId,
      patientId,
    });
  };

  const handleRemoveArticle = () => {
    setSelectedArticle(null);
    setArticleNotFoundError("");
  };

  const handleOpenPicker = () => {
    if (isSms) return;
    setIsPickerOpen(true);
  };

  const handleInputChange = (value: string) => {
    setMessageContent(value);
  };

  const handleTitleChange = (value: string) => {
    if (value !== "") {
      setErrorTitle("");
    }
    setMessageTitle(value);
  };

  // Inside a conversation (the "+" → Custom path) a "No answer" adds nothing a plain follow-up does not, so
  // it is only offered for a new message, where it opens a thread nobody can reply to.
  const optionsMessageType = [
    {
      label: t("patientPage_messages_create_typeFreeText"),
      value: "FreeText",
      key: uuidv4(),
    },
    {
      label: t("patientPage_messages_create_typeOptions"),
      value: "Options",
      key: uuidv4(),
    },
    ...(threadId
      ? []
      : [
          {
            label: t("patientPage_messages_create_typeNoAnswer"),
            value: "NoAnswer",
            key: uuidv4(),
          },
        ]),
  ];

  const optionsTimeFrame = [
    {
      label: t("patientPage_messages_create_within24h"),
      value: "TwentyFourHours",
      key: uuidv4(),
    },
    {
      label: t("patientPage_messages_create_within48h"),
      value: "FortyEightHours",
      key: uuidv4(),
    },
    {
      label: t("patientPage_messages_create_within72h"),
      value: "SeventyTwoHours",
      key: uuidv4(),
    },
    {
      label: t("patientPage_messages_create_withinOneWeek"),
      value: "OneWeek",
      key: uuidv4(),
    },
  ];

  const optionsSendMethod = [];
  if (hasRegisteredDevices) {
    // Allow App if the patient has at least one registered device
    optionsSendMethod.push({
      label: t("patientPage_messages_create_methodApp"),
      value: "App",
      key: uuidv4(),
    });
  }

  if (patientPhoneNumber !== null && patientPhoneNumber !== "") {
    // Allow SMS if the patient has a phone number
    /* SMS not available currently
    optionsSendMethod.push({
      label: t("patientPage_messages_create_methodSms"),
      value: "SMS",
      key: uuidv4(),
    });
    */
  }

  const handleMessageOptionChange = (id: number, newValue: string) => {
    setErrorOptionValue("");
    const newUserEnteredOptions = [...userEnteredOptions];
    const option = newUserEnteredOptions.find((a) => a.id === id);
    if (option) {
      option.value = newValue;
    }
    setUserEnteredOptions(newUserEnteredOptions);
  };

  const checkIfIdExists = (newId: number) => {
    const option = userEnteredOptions.find((a) => a.id === newId);
    if (option) {
      return true;
    }
    return false;
  };

  const getNewId = () => {
    let newId = userEnteredOptions.length + 1;
    if (!checkIfIdExists(newId)) {
      return newId;
    }
    for (let i = 1; i <= 50; i += 1) {
      if (!checkIfIdExists(i)) {
        newId = i;
        break;
      }
    }
    return newId;
  };

  const onClickFocus = (id: number) => {
    refsByKey[id]?.focus();
    refsByKey[id]?.select();
  };

  useEffect(() => {
    if (updatingRefId !== 0) {
      onClickFocus(updatingRefId);
      setUpdatingRefId(0);
    }
  }, [updatingRefId]);

  const handleAddNewOption = () => {
    if (userEnteredOptions.length > 50) {
      // Limit the number of options at 50
      return;
    }
    const newId = getNewId();

    setUserEnteredOptions([
      ...userEnteredOptions,
      {
        id: newId,
        value: t("patientPage_messages_create_optionPlaceholder", {
          number: newId,
        }),
        name: `option-${newId}`,
        key: uuidv4(),
      },
    ]);
    // Set the ID to use in focus
    setUpdatingRefId(newId);
  };

  const handleDeleteOption = (optionId: number) => {
    setErrorOptionValue("");
    setUserEnteredOptions(userEnteredOptions.filter((a) => a.id !== optionId));
  };

  const sendMessageApi = async (dataToCreateMessage: ICreateMessageProps) => {
    setIsMessageLoading(true);
    setArticleNotFoundError("");

    const result = await messageService.createMessage(dataToCreateMessage);
    const { status, data } = result;

    if (status >= 200 && status < 300) {
      if (dataToCreateMessage.articleId) {
        mixpanelActions.track("HCP Sent Message With Article", {
          articleId: dataToCreateMessage.articleId,
          patientId: dataToCreateMessage.patientId,
          sentProvider: dataToCreateMessage.sentProvider,
        });
        setNotification(
          t("patientPage_messages_create_sentWithArticle"),
          "success"
        );
      } else {
        setNotification(t("patientPage_messages_create_sent"), "success");
      }
      (onSent ?? onClose)();
      return;
    }

    if (status === 400) {
      const errorCode = (data as any)?.errors?.[0]?.code as string | undefined;
      if (errorCode === "ArticleNotFound") {
        setArticleNotFoundError(t("patientPage_messages_create_articleGone"));
        setIsMessageLoading(false);
        return;
      }
    }

    setNotification(t("patientPage_messages_create_error"), "error");
    (onCancel ?? onClose)();
  };

  const handleCreateMessage = async () => {
    mixpanelActions.track("User Action: Save Message");
    let error = false;

    if (messageTitle === "") {
      setErrorTitle(t("patientPage_messages_create_headingRequired"));
      error = true;
    }

    if (messageType === "Options") {
      // Check for duplicate values in option values
      const valueArr = userEnteredOptions.map(function getValue(item) {
        return item.value;
      });
      const duplicateItems = valueArr.filter(
        (item, index) => valueArr.indexOf(item) !== index
      );
      if (duplicateItems.length > 0) {
        setErrorOptionValue(
          t("patientPage_messages_create_duplicateOption", {
            option: duplicateItems,
          })
        );
        error = true;
      }
    }

    if (error) {
      // Error Message will be displayed
      return;
    }

    const articleId = selectedArticle?.articleId;

    if (messageType === "Options") {
      // Create the options array with just the text
      const messageOptions = [];
      for (let i = 0; i < userEnteredOptions.length; i += 1) {
        messageOptions.push({
          Text: `${userEnteredOptions[i].value}`,
          Index: i,
        });
      }
      const dataToCreateMessage: ICreateMessageProps = {
        messageBody: messageContent,
        messageTitle,
        timeFrame: responseTimeFrame,
        responseType: messageType,
        patientId,
        sentProvider: sendMethod,
        responseOptions: messageOptions,
        articleId,
        threadId,
      };

      sendMessageApi(dataToCreateMessage);
    } else if (messageType === "NoAnswer") {
      // No response time for NoAnswer
      const dataToCreateMessage: ICreateMessageProps = {
        messageBody: messageContent,
        messageTitle,
        responseType: messageType,
        patientId,
        sentProvider: sendMethod,
        articleId,
        threadId,
      };

      sendMessageApi(dataToCreateMessage);
    } else {
      const dataToCreateMessage: ICreateMessageProps = {
        messageBody: messageContent,
        messageTitle,
        timeFrame: responseTimeFrame,
        responseType: messageType,
        patientId,
        sentProvider: sendMethod,
        articleId,
        threadId,
      };

      sendMessageApi(dataToCreateMessage);
    }
  };

  const handleCancelClick = () => {
    // In the thread flow this returns to the conversation with the draft intact, rather than
    // dropping the staff member out of the modal entirely.
    (onCancel ?? onClose)();
  };

  return optionsSendMethod.length > 0 ? (
    <Container>
      <Title>{t("patientPage_messages_create_title")}</Title>
      <FirstRowContainer>
        <TextFieldContainer>
          <TextField
            label=""
            name="title"
            value={messageTitle}
            placeholder={t("patientPage_messages_create_headingPlaceholder")}
            errorMessage={errorTitle}
            onChange={(event) => handleTitleChange(event.target.value)}
          />
        </TextFieldContainer>
        <DropdownContainer>
          <SelectDropdown
            options={optionsMessageType}
            value={messageType}
            placeholder={t("patientPage_messages_create_selectType")}
            width={150}
            onValueChange={(value: string | string[]) => {
              setMessageType(value.toString());
            }}
          />
        </DropdownContainer>
      </FirstRowContainer>
      <TextAreaContainer>
        <TextArea
          cols={1000}
          rows={5}
          value={messageContent}
          placeholder={t("patientPage_messages_create_bodyPlaceholder")}
          onChange={(event) => handleInputChange(event.target.value)}
        />
      </TextAreaContainer>
      {selectedArticle && (
        <SelectedArticleCard invalid={Boolean(articleNotFoundError)}>
          <SelectedArticleIcon>
            <Book width={20} height={20} />
          </SelectedArticleIcon>
          <SelectedArticleText>
            <SelectedArticleLabel>
              {t("patientPage_messages_create_attachedArticle")}
            </SelectedArticleLabel>
            <SelectedArticleLink
              href={`/careinfo/view/${selectedArticle.articleId}`}
              target="_blank"
              rel="noopener noreferrer"
              disabled={Boolean(articleNotFoundError)}
              title={selectedArticle.articleName}
            >
              {selectedArticle.articleName}
            </SelectedArticleLink>
            {articleNotFoundError && (
              <ArticleInlineError>{articleNotFoundError}</ArticleInlineError>
            )}
          </SelectedArticleText>
          <RemoveArticleButton
            type="button"
            aria-label={t("patientPage_messages_create_removeArticle")}
            onClick={handleRemoveArticle}
          >
            <Close width={14} height={14} />
          </RemoveArticleButton>
        </SelectedArticleCard>
      )}
      {!selectedArticle && (
        <AttachArticleRow>
          <Tooltip
            title={
              isSms ? t("patientPage_messages_smsNoArticleLink") : undefined
            }
            placement="top"
          >
            <AttachArticleButton
              type="button"
              disabled={isSms}
              aria-disabled={isSms}
              onClick={handleOpenPicker}
            >
              <Book width={18} height={18} />
              <span>{t("patientPage_messages_create_attachArticle")}</span>
            </AttachArticleButton>
          </Tooltip>
        </AttachArticleRow>
      )}
      {messageType === "Options" && (
        <OptionsContainer>
          <OptionsContainerScroll>
            {userEnteredOptions.map((option) => (
              <OptionsRowContainer key={option.key}>
                <OptionLabel
                  htmlFor={option.name}
                  onClick={() => onClickFocus(option.id)}
                >
                  <SwitchOff width={16} height={16} />
                </OptionLabel>
                <TextField
                  label=""
                  value={option.value}
                  name={option.name}
                  key={option.key}
                  className={optionTextFieldStyles()}
                  type="text"
                  inputPadding="5px 10px"
                  innerRef={(element: HTMLInputElement) =>
                    setRef(element, option.id)
                  }
                  onChange={(event) =>
                    handleMessageOptionChange(option.id, event.target.value)
                  }
                />
                {userEnteredOptions.length > 1 && (
                  <DeleteButton onClick={() => handleDeleteOption(option.id)}>
                    <Close />
                  </DeleteButton>
                )}
              </OptionsRowContainer>
            ))}
          </OptionsContainerScroll>
          <NewOptionButton onClick={handleAddNewOption}>
            <>
              <SwitchOff width={16} height={16} />
              <p>{t("patientPage_messages_create_addOption")}</p>
            </>
          </NewOptionButton>
          {errorOptionValue !== "" && (
            <ErrorMessage>{errorOptionValue}</ErrorMessage>
          )}
        </OptionsContainer>
      )}
      <hr
        style={{
          marginLeft: "45px",
          marginRight: "35px",
          background: "#E6E6E6",
          height: "1px",
          border: "none",
          marginTop: "8px",
        }}
      />
      <BottomDropdownContainer>
        {messageType === "NoAnswer" ? (
          <SelectDropdown
            options={optionsTimeFrame}
            disabled
            placeholder={t("patientPage_messages_create_selectTimeframe")}
            width={250}
          />
        ) : (
          <SelectDropdown
            options={optionsTimeFrame}
            value={responseTimeFrame}
            placeholder={t("patientPage_messages_create_selectTimeframe")}
            width={250}
            onValueChange={(value: string | string[]) => {
              setResponseTimeFrame(value.toString());
            }}
          />
        )}
        <SendMethodDropdown>
          <SelectDropdown
            options={optionsSendMethod}
            value={sendMethod}
            placeholder={t("patientPage_messages_create_selectMethod")}
            width={100}
            onValueChange={(value: string | string[]) => {
              setSendMethod(value.toString());
            }}
          />
        </SendMethodDropdown>
      </BottomDropdownContainer>
      <SubmitContainer>
        <Button
          isLoading={isMessageLoading}
          label={t("patientPage_messages_create_send")}
          type="button"
          onClick={handleCreateMessage}
        />
        <CancelButton onClick={handleCancelClick}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
      <ArticlePickerModal
        open={isPickerOpen}
        patientId={patientId}
        selectedArticleId={selectedArticle?.articleId}
        onSelect={handleSelectArticle}
        onClose={() => setIsPickerOpen(false)}
      />
    </Container>
  ) : (
    <Container>
      <Title>{t("patientPage_messages_create_blockedTitle")}</Title>
      <ContentContainer>
        {t("patientPage_messages_create_blockedBody")}
      </ContentContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_messages_create_ok")}
          type="button"
          onClick={handleCancelClick}
        />
      </SubmitContainer>
    </Container>
  );
}
